import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiClient } from '../lib/api';

// Helper to extract tool from operation string like "tool:browserless"
function extractToolFromOperation(operation) {
  if (operation?.startsWith('tool:')) {
    return operation.substring(5);
  }
  return null;
}

// Helper to determine step type and name
function getStepInfo(span, index) {
  const tool = extractToolFromOperation(span.operation);
  if (tool) {
    return {
      type: 'tool',
      name: tool,
      label: `Step ${index + 1}: Tool Call`,
      subtitle: `${tool} → ${span.metadata?.output ? 'completed' : 'executed'}`,
    };
  }

  // LLM call
  return {
    type: 'llm',
    name: span.model_id || span.model_name || 'unknown',
    label: `Step ${index + 1}: ${span.operation || 'LLM Call'}`,
    subtitle: `${span.model_id || span.model_name} → ${span.metadata?.output_type || 'response'}`,
  };
}

// Highlight fields in JSON output that have validation errors
function HighlightedOutput({ output, validationErrors }) {
  if (!output) return null;

  let outputStr = typeof output === 'string' ? output : JSON.stringify(output, null, 2);

  // If we have validation errors, highlight the bad fields
  if (validationErrors?.length > 0) {
    validationErrors.forEach(err => {
      const pattern = new RegExp(`"${err.field}":\\s*"([^"]*)"`, 'g');
      outputStr = outputStr.replace(pattern, (match, value) => {
        return `"${err.field}": "<mark class="bg-yellow-500/30 text-yellow-400 px-1 rounded">${value}</mark>" ← hallucinated`;
      });
    });
  }

  return (
    <pre
      className="text-xs bg-zinc-950 p-3 rounded overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: outputStr }}
    />
  );
}

function StepCard({ span, index, isLast, validationErrors }) {
  const [expanded, setExpanded] = useState(span.status === 'error');
  const stepInfo = getStepInfo(span, index);
  const isError = span.status === 'error' || span.status === 'failed';

  // Filter validation errors for this span
  const spanErrors = validationErrors?.filter(e =>
    !e.span_id || e.span_id === span.span_id
  ) || [];
  const hasValidationErrors = spanErrors.length > 0;

  return (
    <div className={`relative ${!isLast ? 'pb-4' : ''}`}>
      {/* Connector line */}
      {!isLast && (
        <div className="absolute left-[11px] top-[24px] bottom-0 w-0.5 bg-zinc-800" />
      )}

      {/* Status dot */}
      <div className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center ${
        isError ? 'bg-red-500/20' : hasValidationErrors ? 'bg-yellow-500/20' : 'bg-green-500/20'
      }`}>
        <div className={`w-3 h-3 rounded-full ${
          isError ? 'bg-red-500' : hasValidationErrors ? 'bg-yellow-500' : 'bg-green-500'
        }`} />
      </div>

      {/* Card */}
      <div className={`ml-10 rounded-lg border ${
        isError ? 'border-red-900 bg-red-950/20' :
        hasValidationErrors ? 'border-yellow-900 bg-yellow-950/20' :
        'border-zinc-800 bg-zinc-900/50'
      }`}>
        {/* Header */}
        <div
          className="p-4 cursor-pointer flex items-center justify-between"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold">{stepInfo.label}</h3>
              <span className={`text-xs px-2 py-0.5 rounded ${
                isError
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'bg-green-500/20 text-green-400 border border-green-500/30'
              }`}>
                {isError ? 'Failed' : 'Success'}
              </span>
              {hasValidationErrors && (
                <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                  Validation Error
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-500 mt-1">{stepInfo.subtitle}</p>
          </div>

          <div className="flex items-center gap-6">
            {/* Stats */}
            <div className="flex gap-6 text-sm">
              <div>
                <div className="text-zinc-500 text-xs">{stepInfo.type === 'tool' ? 'Tool' : 'Model'}</div>
                <div className="font-medium">{stepInfo.name}</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs">Duration</div>
                <div className="font-medium">{(span.duration_ms / 1000).toFixed(1)}s</div>
              </div>
              {stepInfo.type === 'llm' && (
                <div>
                  <div className="text-zinc-500 text-xs">Tokens</div>
                  <div className="font-medium">{(span.input_tokens + span.output_tokens).toLocaleString()}</div>
                </div>
              )}
              {stepInfo.type === 'tool' && span.metadata?.result_count && (
                <div>
                  <div className="text-zinc-500 text-xs">Results</div>
                  <div className="font-medium">{span.metadata.result_count}</div>
                </div>
              )}
            </div>

            {/* Expand icon */}
            {expanded ? (
              <ChevronDown className="w-5 h-5 text-zinc-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-zinc-500" />
            )}
          </div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-zinc-800/50 pt-4 space-y-4">
            {/* Error message */}
            {isError && (span.error_message || span.error_type) && (
              <div className="bg-red-950/50 border border-red-900 rounded p-3">
                <div className="text-red-400 font-semibold text-sm">
                  {span.error_type ? `${span.error_type}: ` : ''}{span.error_message || 'Unknown error'}
                </div>
                {span.metadata?.error_context && (
                  <p className="text-red-400/70 text-sm mt-2">{span.metadata.error_context}</p>
                )}
              </div>
            )}

            {/* Validation errors detail */}
            {hasValidationErrors && (
              <div className="bg-yellow-950/30 border border-yellow-900/50 rounded p-3">
                <div className="text-yellow-400 font-semibold text-sm mb-2">Validation Errors</div>
                {spanErrors.map((err, i) => (
                  <div key={i} className="text-sm text-yellow-400/80">
                    <span className="font-mono bg-yellow-500/20 px-1 rounded">{err.field}</span>: {err.error}
                    {err.value && <span className="text-zinc-500 ml-2">(value: "{err.value}")</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Output display */}
            {span.metadata?.output && (
              <div>
                <div className="text-xs text-zinc-500 mb-2">Output</div>
                <HighlightedOutput
                  output={span.metadata.output}
                  validationErrors={spanErrors}
                />
              </div>
            )}

            {/* Input display for tools */}
            {stepInfo.type === 'tool' && span.metadata?.input && (
              <div>
                <div className="text-xs text-zinc-500 mb-2">Input</div>
                <pre className="text-xs bg-zinc-950 p-3 rounded overflow-x-auto">
                  {typeof span.metadata.input === 'string'
                    ? span.metadata.input
                    : JSON.stringify(span.metadata.input, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ExecutionDetail() {
  const { traceId } = useParams();
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch trace details
    apiClient.getTraceDetail(traceId)
      .then(data => {
        const spans = Array.isArray(data) ? data : data.data || [];
        const root = spans.find(s => !s.parent_id && !s.parent_span_id) || spans[0];

        if (root) {
          // Get child spans sorted by timestamp
          const children = spans
            .filter(s => s.span_id !== root.span_id)
            .sort((a, b) => new Date(a.ts_start || a.timestamp) - new Date(b.ts_start || b.timestamp));

          root.children = children;
          root.model_name = root.model_id || root.model_name;
          root.cost_usd = root.cost_est_usd || root.cost_usd || 0;
          root.timestamp = root.ts_start || root.timestamp;

          // Normalize children
          root.children.forEach(c => {
            c.model_name = c.model_id || c.model_name;
            c.cost_usd = c.cost_est_usd || c.cost_usd || 0;
            c.timestamp = c.ts_start || c.timestamp;
            c.metadata = c.metadata || {};
          });
        }
        setExecution(root);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });

    // Fetch validation errors (if endpoint exists)
    apiClient.getValidationErrors?.(traceId)
      .then(data => {
        setValidationErrors(data?.errors || []);
      })
      .catch(() => {
        // Endpoint may not exist yet, that's OK
        setValidationErrors([]);
      });
  }, [traceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-zinc-800 rounded w-1/3"></div>
            <div className="h-4 bg-zinc-800 rounded w-1/2"></div>
            <div className="grid grid-cols-5 gap-4 mt-8">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-zinc-800 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/executions')}
            className="text-zinc-400 hover:text-white transition-colors mb-4"
          >
            ← Back to Executions
          </button>
          <div className="text-center py-20 text-zinc-500">
            Execution not found
          </div>
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const allSpans = [execution, ...execution.children];
  const totalCost = allSpans.reduce((sum, s) => sum + (s.cost_usd || 0), 0);
  const totalDuration = allSpans.reduce((sum, s) => sum + (s.duration_ms || 0), 0);
  const hasError = allSpans.some(s => s.status === 'error' || s.status === 'failed');
  const failedStep = execution.children.findIndex(s => s.status === 'error' || s.status === 'failed');

  // Extract goal from metadata or operation
  const goal = execution.metadata?.goal || execution.goal || execution.operation || 'unknown';

  // Find primary tool used
  const toolSpan = execution.children.find(s => s.operation?.startsWith('tool:'));
  const primaryTool = toolSpan ? extractToolFromOperation(toolSpan.operation) : null;

  // Find primary model used
  const llmSpan = execution.children.find(s => s.model_id && !s.operation?.startsWith('tool:'));
  const primaryModel = llmSpan?.model_id || execution.model_id || 'unknown';

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Back Button */}
        <button
          onClick={() => navigate('/executions')}
          className="text-zinc-400 hover:text-white transition-colors"
        >
          ← Back to Executions
        </button>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Execution Detail</h1>
            <p className="text-sm text-zinc-500 font-mono mt-1">{execution.trace_id}</p>
          </div>
          <span className={`px-3 py-1 rounded text-sm font-medium ${
            hasError
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-green-500/20 text-green-400 border border-green-500/30'
          }`}>
            {hasError ? 'Error' : 'Success'}
          </span>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-xs text-zinc-500 mb-1">Goal</div>
            <div className="font-semibold truncate">{goal}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-xs text-zinc-500 mb-1">Model</div>
            <div className="font-semibold truncate">{primaryModel}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-xs text-zinc-500 mb-1">Tool</div>
            <div className="font-semibold truncate">{primaryTool || '—'}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-xs text-zinc-500 mb-1">Duration</div>
            <div className="font-semibold">{(totalDuration / 1000).toFixed(1)}s</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-xs text-zinc-500 mb-1">Failed Step</div>
            <div className={`font-semibold ${failedStep >= 0 ? 'text-red-400' : 'text-zinc-500'}`}>
              {failedStep >= 0 ? `Step ${failedStep + 1}` : '—'}
            </div>
          </div>
        </div>

        {/* Execution Chain */}
        <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
          <h2 className="text-lg font-bold mb-6">Execution Chain</h2>

          <div className="space-y-0">
            {execution.children.map((span, idx) => (
              <StepCard
                key={span.span_id}
                span={span}
                index={idx}
                isLast={idx === execution.children.length - 1}
                validationErrors={validationErrors}
              />
            ))}
          </div>

          {execution.children.length === 0 && (
            <div className="text-center py-8 text-zinc-500">
              No child spans found for this execution
            </div>
          )}
        </div>

        {/* Cost Summary */}
        <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
          <h2 className="text-lg font-bold mb-4">Summary</h2>
          <div className="grid grid-cols-4 gap-6">
            <div>
              <div className="text-xs text-zinc-500 mb-1">Total Cost</div>
              <div className="text-2xl font-bold">${totalCost.toFixed(4)}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 mb-1">Total Duration</div>
              <div className="text-2xl font-bold">{(totalDuration / 1000).toFixed(1)}s</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 mb-1">Steps</div>
              <div className="text-2xl font-bold">{execution.children.length}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 mb-1">Status</div>
              <div className={`text-2xl font-bold ${hasError ? 'text-red-400' : 'text-green-400'}`}>
                {hasError ? 'Failed' : 'Success'}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
